import { ToastrService } from 'ngx-toastr';
import { forkJoin, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { SnCamera, SnLocation } from '@lib/api/entities';
import { SnCameraGridsAPI, SnCamerasAPI } from '@lib/api/services';
import { SnFormFieldConfig } from '@lib/form';
import { ISnHttpSelectFormFieldConfig } from '@lib/form/http-select';
import { SnFloorFieldsGeneratorService } from '@lib/locations/services';
import { ISnListResponse } from 'libs/api/src/lib/interfaces/list-response.interface';

@Component({
  templateUrl: './cameras-by-floor-dialog.component.html',
  styleUrls: ['./cameras-by-floor-dialog.component.scss'],
})
export class CamerasByFloorDialogComponent implements OnInit, OnDestroy {
  public form: UntypedFormGroup = new UntypedFormGroup({});
  public formModel: any = {};
  public formFields: SnFormFieldConfig[];
  public isLoading: boolean = false;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      location: SnLocation;
      params: any;
    },
    private readonly _dialogRef: MatDialogRef<CamerasByFloorDialogComponent>,
    private readonly _floorFieldsGenerator: SnFloorFieldsGeneratorService,
    private readonly _camerasAPI: SnCamerasAPI,
    private readonly _cameraGridsAPI: SnCameraGridsAPI,
    private readonly _toastrService: ToastrService,
    private readonly _cd: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this._setForm();
    this.formModel.location = this.data.location.id;
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public submitForm(): void {
    const requests$ = [];
    this.formModel.floor.forEach((floor) => {
      requests$.push(this._fetchCamerasAndCreateGrid(floor));
    });
    forkJoin([...requests$])
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (responses) => {
          responses.forEach((response: ISnListResponse<SnCamera>) => {
            if (response?.results?.length) {
              const floorName = response.results[0].floor.name;
              const cameraIds = response.results.map((camera) => camera.id);
              this._sendToGrid(cameraIds, floorName);
            } else {
              this.isLoading = false;
              this._toastrService.error('No cameras found.');
              this._dialogRef.close();
            }
          });
          this._cd.detectChanges();
        },
        error: () => {
          this.isLoading = false;
          this._dialogRef.close();
          this._cd.detectChanges();
        },
      });
  }

  private _fetchCamerasAndCreateGrid(floorId: string): Observable<ISnListResponse<SnCamera>> {
    const payload = {
      ...this.data.params,
      location: this.data.location.id,
      floor: floorId,
    };
    this.isLoading = true;
    return this._camerasAPI.getCameraList(payload);
  }

  private _sendToGrid(cameraIds: string[], floor: string): void {
    if (cameraIds) {
      const now = new Date();
      const gridSize = this._getGridGeometry(cameraIds.length);
      const floorName = `${floor}-${now.toLocaleDateString()}-${now.toLocaleTimeString()}`;
      const name = floorName.length >= 40 ? `${floorName.substring(0, 40)}...` : floorName;
      const submit$ = this._cameraGridsAPI.createCameraGrid({
        name,
        isPrivate: true,
        autoplay: true,
        cameras: cameraIds,
        meta: {
          order: cameraIds,
          geometry: gridSize,
        },
      });

      submit$.pipe(takeUntil(this._destroy$)).subscribe({
        next: (grid) => {
          const route = `/camera-grids/${grid.id}`;
          window.open(route, '_blank');
          this.isLoading = false;
          this._dialogRef.close();
          this._cd.detectChanges();
        },
        error: () => {
          this.isLoading = false;
          this._dialogRef.close();
          this._cd.detectChanges();
        },
      });
    }
  }

  private _getGridGeometry(size: number): { cols: string; rows: string } {
    if (size <= 4) {
      return { cols: '2', rows: '2' };
    } else if (size > 4 && size <= 6) {
      return { cols: '3', rows: '2' };
    } else if (size > 4 && size <= 9) {
      return { cols: '3', rows: '3' };
    }
    return { cols: '4', rows: '4' };
  }

  private _setForm(): void {
    this.formFields = [
      {
        key: 'location',
        type: 'select',
        templateOptions: {
          appearance: 'outline',
          floatLabel: 'always',
          placeholder: 'Location',
          disabled: true,
          label: 'Location',
          options: [
            {
              label: this.data.location.name,
              value: this.data.location.id,
            },
          ],
          valueProp: 'value',
          labelProp: 'label',
          multiple: false,
        },
      },
      this._floorFieldsGenerator.getFloorSelectField('location', {
        templateOptions: {
          required: true,
          multiple: true,
        },
        hooks: {
          onInit: (field: ISnHttpSelectFormFieldConfig) => {
            field.parent.formControl
              .get('location')
              .valueChanges.pipe(distinctUntilChanged(), takeUntil(this._destroy$))
              .subscribe(() => {
                field.reset();
              });
          },
        },
      }),
    ];
  }
}
